import { el } from "./dom";
import { fillDurationSelect, fillTimeSelect, labeled, mountDatePicker } from "./datepicker";
import { mountPollBuilder } from "./pollbuilder";
import { changedEventFields, deadlineInstant, deadlineWallTime, pollsForSave, type EventFields } from "../shared/present";
import type { EventDef } from "../shared/types";

export interface EventBody extends EventFields {
  slotMin: 30;
}

export interface EventForm {
  root: HTMLElement;
  read(): EventBody | { error: string };
  /** Fills the fields AND records `def` itself as the new baseline (not a read-back of the DOM,
   * because filling skips a focused field). */
  set(def: EventFields): void;
  /** Derived, not a flag: true when `changedEventFields(baseline, currentFields)` is not empty, or
   * when the poll builder's raw structure differs from the baseline's polls, or when the current
   * fields cannot be read. No caller has to remember to mark this. */
  isDirty(): boolean;
}

function zoneList(extra?: string): string[] {
  const viewer = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const zones = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [viewer, "UTC"];
  const out = zones.slice();
  if (extra && !out.includes(extra)) out.unshift(extra);
  if (!out.includes(viewer)) out.unshift(viewer);
  return out;
}

/** The baseline before the first `set()` call. Matches the form's own just-mounted defaults field
 * for field, so a form nobody has filled from yet reads as clean rather than permanently dirty. */
function emptyFields(): EventFields {
  return {
    title: "",
    note: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    dates: [],
    dayStartMin: 9 * 60,
    dayEndMin: 17 * 60,
    durationMin: 60,
    polls: [],
    deadline: null,
    expected: [],
  };
}

export function mountEventForm(opts: {
  titleTestId?: string;
  noteTestId?: string;
  tzTestId?: string;
  startTestId?: string;
  endTestId?: string;
  lengthTestId?: string;
  deadlineTestId?: string;
  includeExpected?: boolean;
  onDirty?: () => void;
}): EventForm {
  let baseline: EventFields = emptyFields();
  const title = el("input", { type: "text", maxlength: "80", "data-testid": opts.titleTestId });
  const note = el("textarea", { maxlength: "500", "data-testid": opts.noteTestId });
  const tzSel = el("select", { "data-testid": opts.tzTestId });
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  for (const z of zoneList(viewerTz)) {
    const o = el("option", { value: z, text: z });
    if (z === viewerTz) o.selected = true;
    tzSel.append(o);
  }
  const dates = mountDatePicker(tzSel.value || viewerTz, [], () => notifyChange());
  const startSel = el("select", { "data-testid": opts.startTestId });
  const endSel = el("select", { "data-testid": opts.endTestId });
  const lenSel = el("select", { "data-testid": opts.lengthTestId });
  fillTimeSelect(startSel, 9 * 60, false);
  fillTimeSelect(endSel, 17 * 60, true);
  fillDurationSelect(lenSel, 60);
  const deadline = el("input", { type: "datetime-local", "data-testid": opts.deadlineTestId });
  const expected = el("textarea");
  const builder = mountPollBuilder([], () => notifyChange());

  function notifyChange(): void {
    opts.onDirty?.();
  }

  title.addEventListener("input", notifyChange);
  note.addEventListener("input", notifyChange);
  tzSel.addEventListener("change", () => {
    dates.setTimezone(tzSel.value);
    notifyChange();
  });
  startSel.addEventListener("change", notifyChange);
  endSel.addEventListener("change", notifyChange);
  lenSel.addEventListener("change", notifyChange);
  deadline.addEventListener("input", notifyChange);
  expected.addEventListener("input", notifyChange);

  const root = el("div", { class: "event-form" });
  root.append(
    labeled("Title", title),
    labeled("Note", note),
    labeled("Timezone", tzSel),
    el("p", { class: "muted", text: "Detected timezone is shown. Dates are in this zone." }),
    el("div", null, el("span", { class: "label", text: "Dates (next 8 weeks)" }), dates.root),
    el("div", { class: "row" }, labeled("Start", startSel), labeled("End", endSel), labeled("Meeting length", lenSel)),
    labeled("Deadline (optional)", deadline),
  );
  if (opts.includeExpected) {
    root.append(labeled("Expected names (one per line)", expected));
  }
  root.append(el("h2", { text: opts.includeExpected ? "Polls" : "Polls (optional)" }), builder.root);

  function readInternal(): EventBody | { error: string } {
    const timezone = tzSel.value;
    const dayStartMin = Number(startSel.value);
    const dayEndMin = Number(endSel.value);
    const durationMin = Number(lenSel.value);
    if (durationMin > dayEndMin - dayStartMin) {
      return { error: "The meeting is longer than the daily window." };
    }
    const picked = dates.getDates();
    if (picked.length < 1) return { error: "Pick at least one date." };
    let deadlineIso: string | null = null;
    if (deadline.value) {
      deadlineIso = deadlineInstant(deadline.value, timezone);
      if (deadlineIso === null) {
        return { error: `That time does not exist in ${timezone} because of daylight saving.` };
      }
    }
    // An unfinished poll is an error on screen, never a quiet deletion (see pollsForSave).
    const polls = pollsForSave(builder.getRawPolls());
    if (!polls.ok) return { error: polls.error };
    return {
      title: title.value,
      note: note.value,
      timezone,
      dates: picked,
      dayStartMin,
      dayEndMin,
      durationMin,
      slotMin: 30,
      polls: polls.polls,
      deadline: deadlineIso,
      expected: expected.value
        .split(/\n|,/)
        .map((n) => n.trim())
        .filter(Boolean),
    };
  }

  return {
    root,
    read: readInternal,
    set(def) {
      if (def.timezone && ![...tzSel.options].some((o) => o.value === def.timezone)) {
        tzSel.prepend(el("option", { value: def.timezone, text: def.timezone }));
      }
      // set() only ever runs while the form is clean or right after a save (see isDirty()'s
      // callers), so a focused control holds nothing of the user's: clicking into a field is not
      // editing it. Every control whose value differs from the new one is assigned, focused or
      // not, so a live update from another organizer reaches a field this one merely clicked into
      // instead of leaving it holding a stale value that would later read as an edit nobody typed.
      const assign = (field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string): void => {
        if (field.value !== value) field.value = value;
      };
      assign(title, def.title);
      assign(note, def.note);
      assign(tzSel, def.timezone);
      dates.setTimezone(def.timezone);
      dates.setDates(def.dates);
      assign(startSel, String(def.dayStartMin));
      assign(endSel, String(def.dayEndMin));
      assign(lenSel, String(def.durationMin));
      const wall = def.deadline ? deadlineWallTime(def.deadline, def.timezone) : "";
      assign(deadline, wall);
      if (opts.includeExpected) assign(expected, def.expected.join("\n"));
      builder.setPolls(def.polls);
      baseline = def;
    },
    isDirty() {
      const current = readInternal();
      if ("error" in current) return true;
      if (Object.keys(changedEventFields(baseline, current)).length > 0) return true;
      return JSON.stringify(builder.getRawPolls()) !== JSON.stringify(baseline.polls);
    },
  };
}

export function defFromEvent(ev: EventDef, expected: string[] = []): EventFields {
  return {
    title: ev.title,
    note: ev.note,
    timezone: ev.timezone,
    dates: [...ev.dates],
    dayStartMin: ev.dayStartMin,
    dayEndMin: ev.dayEndMin,
    durationMin: ev.durationMin,
    polls: ev.polls,
    deadline: ev.deadline,
    expected,
  };
}
